import "../styles/people-families.css";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import AdminLayout from "../components/AdminLayout";
import {
  ArrowLeft, Home, Users, MapPin, Mail, Phone,
  Trophy, DollarSign, AlertCircle, ClipboardList, MessageSquare,
  CheckCircle2, FileWarning, ShieldCheck, Receipt, Clock, FileText, Download
} from "lucide-react";
import { format, differenceInYears } from "date-fns";
import axios from "axios";
import { toast } from "sonner";

const EmbeddedLayout = ({ children }) => <>{children}</>;

const AdminHouseholdDetail = ({ embedded = false, routeBase = "/admin/people", householdId: householdIdProp }) => {
  const PageLayout = embedded ? EmbeddedLayout : AdminLayout;
  const { householdId: routeHouseholdId } = useParams();
  const householdId = householdIdProp || routeHouseholdId;
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [household, setHousehold] = useState(null);
  const [activeSection, setActiveSection] = useState("household-overview");

  useEffect(() => {
    fetchHousehold();
  }, [householdId, token]);

  const fetchHousehold = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/admin/households/${householdId}`, { headers });
      setHousehold(response.data);
    } catch (error) {
      console.error("Error fetching household:", error);
      toast.error("Failed to load household");
      navigate(routeBase.startsWith("/org/") ? `${routeBase}/households` : "/admin/households");
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return null;
    return differenceInYears(new Date(), new Date(dob));
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const downloadDocument = async (document) => {
    try {
      const response = await axios.get(`${API}/family-documents/${document.document_id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = document.original_filename || "family-document";
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Document could not be downloaded");
    }
  };

  const readiness = {
    missingContacts: household?.members?.filter((member) => !member.email && !member.phone).length || 0,
    incompletePlayers: household?.players?.filter((player) => !player.date_of_birth).length || 0,
    missingDocuments: household?.registrations?.reduce((count, registration) => count + (registration.missing_documents?.length || 0), 0) || 0,
  };
  const readinessIssueCount = readiness.missingContacts + readiness.incompletePlayers + readiness.missingDocuments;

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-slate-900"></div>
        </div>
      </PageLayout>
    );
  }

  if (!household) {
    return (
      <PageLayout title="Household Details">
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900">Household not found</h2>
          <Button onClick={() => navigate(routeBase.startsWith("/org/") ? `${routeBase}/households` : "/admin/households")} className="mt-4 rounded-2xl">
            Back to Households
          </Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title={household.name}>
      <div className="people-families space-y-5" data-testid="admin-household-detail">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(routeBase.startsWith("/org/") ? `${routeBase}/households` : "/admin/households")}
          className="text-slate-500 hover:text-slate-900 -ml-2 rounded-2xl"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Households
        </Button>

        {/* Header Card */}
        <div className="bg-white border border-slate-200/60 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center">
              <Home className="w-6 h-6 text-slate-500" />
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-semibold text-slate-900">{household.name}</h1>
              <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-500">
                {household.address && (
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    {household.address}, {household.city}, {household.state} {household.zip_code}
                  </span>
                )}
              </div>
            </div>
            <div className="hidden items-center gap-2 sm:flex">
              {household.members?.[0]?.email && (
                <Button variant="outline" asChild className="rounded-xl">
                  <a href={`mailto:${household.members[0].email}`}>
                    <Mail className="mr-2 h-4 w-4" /> Email household
                  </a>
                </Button>
              )}
              <Button onClick={() => scrollToSection("household-payments")} className="rounded-xl bg-teal-700 hover:bg-teal-800">
                <DollarSign className="mr-2 h-4 w-4" /> View balance
              </Button>
            </div>
          </div>
        </div>

        <div className="flex gap-1 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-1" aria-label="Household workspace sections">
          {[
            { id: "household-overview", label: "Overview", icon: Home },
            { id: "household-family", label: "Family", icon: Users },
            { id: "household-registrations", label: "Registrations", icon: ClipboardList },
            { id: "household-payments", label: "Payments", icon: DollarSign },
            { id: "household-documents", label: "Documents", icon: FileText },
            { id: "household-communications", label: "Communications", icon: MessageSquare },
          ].map((section) => {
            const SectionIcon = section.icon;
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => scrollToSection(section.id)}
                aria-current={activeSection === section.id ? "page" : undefined}
                className={`flex min-w-fit items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium ${activeSection === section.id ? "bg-teal-50 text-teal-800" : "text-slate-600 hover:bg-slate-50 hover:text-slate-950"}`}
              >
                <SectionIcon className="h-4 w-4" /> {section.label}
              </button>
            );
          })}
        </div>

        <section className="household-readiness-strip" aria-labelledby="household-readiness-heading" data-testid="household-readiness-strip">
          <div>{readinessIssueCount ? <FileWarning aria-hidden="true" /> : <CheckCircle2 aria-hidden="true" />}<span><small>Player readiness</small><strong id="household-readiness-heading">{readinessIssueCount ? `${readinessIssueCount} items need follow-up` : "Household is ready"}</strong></span></div>
          <dl><div><dt>Missing contact</dt><dd>{readiness.missingContacts}</dd></div><div><dt>Player profile</dt><dd>{readiness.incompletePlayers}</dd></div><div><dt>Documents</dt><dd>{readiness.missingDocuments}</dd></div></dl>
          <p><ShieldCheck aria-hidden="true" /> Medical details remain limited to authorized administrators and roster workflows.</p>
        </section>

            <section id="household-family" className="pf-members bg-white border border-slate-200 rounded-lg">
              <h2 className="p-5 text-2xl font-semibold">Household members</h2>
              <div className="pf-household-head" aria-hidden="true"><span>Name</span><span>Household role</span><span>Contact</span><span>Connection</span></div>
              <div id="household-communications">
                {(household.members || []).map((member, index) => <div className="pf-household-row" key={member.member_id || index}>
                  <div>{member.first_name} {member.last_name}</div>
                  <div><span className="pf-mobile-label">Household role</span><span className="pf-type">{member.role || "Household member"}</span></div>
                  <div><span className="pf-mobile-label">Contact</span>{member.email ? <a href={`mailto:${member.email}`}>{member.email}</a> : "Email not provided"}{member.phone && <small><a href={`tel:${member.phone}`}>{member.phone}</a></small>}</div>
                  <div>{member.is_primary ? "Primary contact" : "Household member"}</div>
                </div>)}
                {(household.players || []).map(player => <div className="pf-household-row" key={player.player_id}>
                  <div>{player.photo_url && <img className="pf-member-photo" loading="lazy" src={player.photo_url} alt="" />}{player.first_name} {player.last_name}<small>{player.date_of_birth && `${calculateAge(player.date_of_birth)} years old`}{player.gender && ` · ${player.gender}`}</small></div>
                  <div><span className="pf-mobile-label">Household role</span><span className="pf-type">Player</span></div>
                  <div><span className="pf-mobile-label">Contact</span>Contact through household</div>
                  <div>Household-linked player<small>{player.registrations?.length || 0} registrations</small></div>
                </div>)}
                {!household.members?.length && <p className="p-4">No members found</p>}
                {!household.players?.length && <p className="p-4">No players in this household</p>}
              </div>
              <p className="p-5 text-sm text-slate-600">Household membership does not establish individual custody or permission to act for a player.</p>
            </section>

        <div id="household-overview" className="grid scroll-mt-6 grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-5">
            {/* Registrations */}
            <div id="household-registrations" className="scroll-mt-6 bg-white border border-slate-200/60 rounded-2xl p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Registration History</h2>
              {household.registrations?.length > 0 ? (
                <div className="space-y-3">
                  {household.registrations.map((reg) => (
                    <div
                      key={reg.registration_id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-white border border-slate-200 rounded-xl flex items-center justify-center">
                          <Trophy className="w-5 h-5 text-slate-400" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{reg.program_name || reg.program?.name || "Program"}</p>
                          <p className="text-sm text-slate-500">
                            {reg.player_name || `${reg.player?.first_name || ""} ${reg.player?.last_name || ""}`.trim()}
                          </p>
                          {reg.missing_documents?.length > 0 && <p className="mt-1 text-xs font-medium text-violet-700">Missing: {reg.missing_documents.join(", ")}</p>}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${
                          reg.status === 'confirmed' ? 'bg-emerald-50 text-emerald-700' :
                          reg.status === 'waitlisted' ? 'bg-amber-50 text-amber-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {reg.status}
                        </span>
                        <p className="text-sm text-slate-500 mt-1">
                          {reg.payment_status === 'paid' ? (
                            <span className="text-emerald-600">✓ ${reg.amount} paid</span>
                          ) : (
                            <span className="text-amber-600">${reg.amount} pending</span>
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-6">No registrations yet</p>
              )}
            </div>

            <div id="household-documents" className="scroll-mt-6 bg-white border border-slate-200/60 rounded-2xl p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Family Documents</h2>
              {household.documents?.length > 0 ? (
                <div className="space-y-3">
                  {household.documents.map((document) => (
                    <div key={document.document_id} className="flex items-center justify-between gap-4 rounded-2xl bg-slate-50 p-4">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white"><FileText className="h-5 w-5 text-slate-500" /></div>
                        <div className="min-w-0"><p className="truncate font-medium text-slate-900">{document.original_filename || document.document_type}</p><p className="text-sm text-slate-500">{document.player_name} · {(document.document_type || "document").replaceAll("_", " ")} · {(document.status || "under review").replaceAll("_", " ")}</p></div>
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={() => downloadDocument(document)} className="shrink-0 rounded-xl"><Download className="mr-2 h-4 w-4" /> Download</Button>
                    </div>
                  ))}
                </div>
              ) : <p className="text-slate-500 text-center py-6">No family documents are related to this organization.</p>}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            {/* Quick Stats */}
            <div id="household-payments" className="scroll-mt-6 bg-white border border-slate-200/60 rounded-2xl p-6">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Financials</h2>
                <button type="button" onClick={() => navigate(routeBase.startsWith("/org/") ? routeBase.replace(/\/people$/, "/finance") : "/admin/financials")} className="text-sm font-medium text-teal-700 hover:text-teal-900">
                  Open collections
                </button>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Total Spent</span>
                  <span className="font-semibold text-slate-900">
                    ${(household.total_paid || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Pending</span>
                  <span className="font-semibold text-amber-600">
                    ${(household.invoice_balance || household.total_pending || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Active Programs</span>
                  <span className="font-semibold text-slate-900">
                    {household.registrations?.filter(r => r.status === 'confirmed').length || 0}
                  </span>
                </div>
              </div>
              <div className="mt-5 border-t border-slate-100 pt-4">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Recent invoices</p>
                {household.invoices?.length ? (
                  <div className="space-y-3">
                    {household.invoices.slice(0, 5).map((invoice) => (
                      <div key={invoice.invoice_id} className="rounded-xl bg-slate-50 p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div><p className="text-sm font-medium text-slate-900">#{invoice.invoice_number}</p><p className="mt-0.5 text-xs text-slate-500">{invoice.description}</p></div>
                          <span className={`rounded-lg px-2 py-1 text-xs font-medium ${invoice.status === "paid" ? "bg-emerald-50 text-emerald-700" : invoice.status === "overdue" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>{invoice.status}</span>
                        </div>
                        <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{invoice.due_date ? format(new Date(invoice.due_date), "MMM d, yyyy") : "No due date"}</span>
                          <span className="font-semibold text-slate-800">${(invoice.total || 0).toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-slate-200 px-3 py-5 text-center"><Receipt className="mx-auto h-5 w-5 text-slate-300" /><p className="mt-2 text-xs text-slate-500">No invoices for this household</p></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default AdminHouseholdDetail;
